// src/services/statusService.ts
import axios from "axios";
import { reportError } from "@/utils/sentry";
import { systemConfigService } from "@/services/systemConfigService";

class StatusService {
  private readonly STATUS_API_URL =
    "https://abdnims.scnu.edu.cn/status/api/status-page/heartbeat/abdnims";

  /**
   * 从配置服务获取服务ID到服务名称的映射
   */
  private getServiceIdMapping(): Record<string, string> {
    const serviceIds: Record<string, string> = {};
    const services = systemConfigService.services;

    for (const [serviceName, serviceConfig] of Object.entries(services)) {
      serviceIds[serviceConfig.id] = serviceName;
    }

    return serviceIds;
  }

  /**
   * 获取所有系统的状态
   * @returns 服务名称到状态代码的映射
   */
  public async getSystemStatus(): Promise<Record<string, number>> {
    try {
      const response = await axios.get(this.STATUS_API_URL);
      const data = response.data;

      if (!data.heartbeatList) {
        throw new Error("无效的响应格式：缺少heartbeatList");
      }

      // 处理心跳数据
      const statusMap: Record<string, number> = {};
      const serviceIdMapping = this.getServiceIdMapping();

      // 遍历所有服务的心跳数据
      for (const [serviceId, heartbeats] of Object.entries(
        data.heartbeatList,
      )) {
        const serviceName = serviceIdMapping[serviceId];
        if (!serviceName) continue;

        // 获取最新状态
        const latestStatus =
          Array.isArray(heartbeats) && heartbeats.length > 0
            ? heartbeats[heartbeats.length - 1].status
            : 2; // 未知状态为2

        statusMap[serviceName] = latestStatus;
      }

      return statusMap;
    } catch (error) {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        component: "StatusService",
        action: "getSystemStatus",
      });
      // 出错时返回空对象，默认所有系统可用
      return {};
    }
  }

  /**
   * 获取已配置的服务ID列表
   * 用于调试和监控
   */
  public getConfiguredServiceIds(): string[] {
    return Object.keys(this.getServiceIdMapping());
  }

  /**
   * 获取已配置的服务名称列表
   * 用于调试和监控
   */
  public getConfiguredServiceNames(): string[] {
    return Object.values(this.getServiceIdMapping());
  }
}

export const statusService = new StatusService();
